import { useCallback } from 'react'
import { usePurchaseOrderClient } from '@frontend/clients/purchase-order.request'
import { useDebounce } from '@frontend/components/hooks'
import { UIListPurchaseOrderQuery } from '@frontend-types/clients'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { PlannedPurchaseOrdersFilters } from '@shared-types/purchase_orders'
import { Identifier, Nullable } from '@shared-types/util'

export const useQueryPurchaseOrders = (filters: UIListPurchaseOrderQuery) => {
  const purchaseOrderClient = usePurchaseOrderClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['all_purchase_orders', debouncedFilters],

    queryFn: async () => {
      return purchaseOrderClient.allPurchaseOrders(debouncedFilters)
    },
  })
}

export const useQueryPurchaseOrdersSearch = (filters: UIListPurchaseOrderQuery) => {
  const purchaseOrderClient = usePurchaseOrderClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['all_purchase_orders', debouncedFilters],

    queryFn: async () => {
      return purchaseOrderClient.allPurchaseOrders(debouncedFilters)
    },
  })
}

export const useQueryGetPurchaseOrder = (id?: Nullable<Identifier>) => {
  const purchaseOrderClient = usePurchaseOrderClient()

  return useQuery({
    queryKey: ['purchase_order', id],

    queryFn: async () => {
      if (!id) {
        return null
      }
      return purchaseOrderClient.getPurchaseOrder(id)
    },
  })
}

export const useQueryGetPlannedPurchaseOrders = (
  companyId: Identifier | undefined,
  filters: PlannedPurchaseOrdersFilters
) => {
  const purchaseOrderClient = usePurchaseOrderClient()
  const debouncedFilters = useDebounce(filters)
  // filters and debouncedFilters diverge while the debounce timer is pending, e.g. right after
  // switching months, before the query itself has re-run for the new range
  const isDebouncingFilters = JSON.stringify(filters) !== JSON.stringify(debouncedFilters)

  const query = useQuery({
    queryKey: ['planned_purchase_order', companyId, debouncedFilters],

    queryFn: async () => {
      if (!companyId) {
        return null
      }
      return purchaseOrderClient.getPlannedPurchaseOrders(companyId, debouncedFilters)
    },
  })

  return { ...query, isPending: query.isPending || isDebouncingFilters }
}

export function useClearAllPurchaseOrdersCache() {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['purchase_order'] }),
      queryClient.invalidateQueries({ queryKey: ['all_purchase_orders'] }),
      queryClient.invalidateQueries({ queryKey: ['purchase_orders_for_day'] }),
      queryClient.invalidateQueries({ queryKey: ['purchase_order_confirmations'] }),
      queryClient.invalidateQueries({ queryKey: ['all_purchase_order_confirmations'] }),
    ])
  }, [queryClient])
}

type PurchaseOrderDayViewFilters = {
  q_sender?: string
  due_date__excluding_booked: string
  receiving_company_id?: string
  limit: number
  page: number
  sort_by?: string | null | undefined
}

export function useQueryPurchaseOrdersDayView(filters: PurchaseOrderDayViewFilters) {
  const purchaseOrderClient = usePurchaseOrderClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['purchase_orders_for_day', debouncedFilters],

    queryFn: async () => {
      if (!debouncedFilters.receiving_company_id) {
        return { results: [] }
      }

      return purchaseOrderClient.allPurchaseOrders(debouncedFilters)
    },
  })
}
